import { ApiEndpoints } from "@shared/api/api-endpoints";
import { communicationApiClient } from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  CancelNotificationResponse,
  Choice,
  CommunicationLog,
  RunHistoryResponse,
  ScheduledNotificationResponse,
} from "./types";

// Helper function to normalize recipient data
const normalizeRecipientData = (data: any) => {
  if (!data) return data;

  return {
    ...data,
    results:
      data.results?.map((item: any) => {
        // Handle the raw API response before transformation
        const recipientData = item.recipientData || {};

        return {
          ...item,
          recipientData: {
            ...recipientData,
            // Ensure 'to' field is always an array
            to: Array.isArray(recipientData.to)
              ? recipientData.to
              : recipientData.to
              ? [recipientData.to]
              : [],
            // Ensure 'cc' field is always an array
            cc: Array.isArray(recipientData.cc)
              ? recipientData.cc
              : recipientData.cc
              ? [recipientData.cc]
              : [],
            // Ensure 'bcc' field is always an array
            bcc: Array.isArray(recipientData.bcc)
              ? recipientData.bcc
              : recipientData.bcc
              ? [recipientData.bcc]
              : [],
          },
        };
      }) || [],
  };
};

// Add your feature-specific API calls here
const getNotificationHistory = async ({
  remoteUtilityId,
  status,
  type,
  date,
  search,
  page = 1, // ✅ ADD THIS - accept page parameter
}: {
  remoteUtilityId: string;
  status?: string;
  type?: string;
  date?: string;
  search?: string;
  page?: number; // ✅ ADD THIS - add page to interface
}): Promise<CommunicationLog> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "notifications",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page.toString()); // ✅ ADD THIS - send page to server
      if (status) {
        query.push("status", status);
      }
      if (type) {
        query.push("channel_type", type);
      }
      if (date) {
        query.push("sort_field", date);
      }
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await communicationApiClient.get(url);

  // Normalize the data BEFORE transformation
  const normalizedData = normalizeRecipientData(response.data);

  // Then transform to camelCase
  return transformJsonToObject<CommunicationLog>(normalizedData);
};

const getRunHistory = async ({
  remoteUtilityId,
  status,
  type,
  date,
  search,
  page = 1,
}: {
  remoteUtilityId: string;
  status?: string;
  type?: string;
  date?: string;
  search?: string;
  page?: number;
}): Promise<RunHistoryResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "run-history",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page.toString());
      if (status) {
        query.push("status", status);
      }
      if (type) {
        query.push("type_of", type);
      }
      if (date) {
        query.push("sort_field", date);
      }
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await communicationApiClient.get(url);
  return transformJsonToObject<RunHistoryResponse>(response.data);
};

// Cancel scheduled notification
const cancelScheduledNotification = async (
  notificationId: number
): Promise<CancelNotificationResponse> => {
  const url = ApiEndpoints.createUrl(
    "communication",
    `cancel-scheduled-notification/${notificationId}`
  );

  const response = await communicationApiClient.put(url);
  return response.data;
};

const getNotificationDetailsForRun = async ({
  remoteUtilityId,
  runId,
  search,
  page = 1,
}: {
  remoteUtilityId: string;
  runId: string;
  search?: string;
  page?: number;
}): Promise<CommunicationLog> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "notifications",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("run_id", runId);
      query.push("page", page.toString());
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await communicationApiClient.get(url);

  // Normalize the data BEFORE transformation
  const normalizedData = normalizeRecipientData(response.data);

  // Then transform to camelCase
  return transformJsonToObject<CommunicationLog>(normalizedData);
};

// Get scheduled notification details for the view page
const getScheduledNotificationDetails = async ({
  remoteUtilityId,
  search,
  page = 1,
  type,
  order,
}: {
  remoteUtilityId: string;
  search?: string;
  page?: number;
  type?: string;
  order?: string;
}): Promise<ScheduledNotificationResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "scheduled-notification",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page.toString());
      if (search) {
        query.push("search_data", search);
      }
      if (type) {
        query.push("type", type);
      }

      if (order) {
        query.push("ordering", order);
      }
    }
  );

  const response = await communicationApiClient.get(url);
  return transformJsonToObject<ScheduledNotificationResponse>(response.data);
};

export const uxHistoryApi = {
  getNotificationHistory,
  getRunHistory,
  cancelScheduledNotification,
  getScheduledNotificationDetails,
  getNotificationDetailsForRun,
};
